// Ring of the prospection banner, ported from the prototype: one arc per call
// of the goal, 24° apart, green once made, orange for a planned callback, pale
// blue for the rest. Past thirty calls the arcs share the ring and thin out.
//
// Props:
// - done: calls made; the centre shows it as is, never capped at the goal.
// - callbacks: planned callbacks, drawn in orange after the green arcs.
// - total: the goal, shown as « /total » after `done`.
// - label (optional): accessible name of the ring.
const RING = { size: 76, stroke: 8, gap: 24 };
const RADIUS = (RING.size - RING.stroke) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const CENTER = RING.size / 2;

const CallsRing = ({ done, callbacks = 0, total, label }) => {
  const arcs = Math.max(1, Math.min(total, 30));
  const scale = total > arcs ? arcs / total : 1;
  // Arcs stop at the goal even when more calls were made.
  const green = Math.min(arcs, Math.round(done * scale));
  const orange = Math.round(callbacks * scale);
  const step = 360 / arcs;
  const gap = arcs === 1 ? 0 : Math.min(RING.gap, step);
  const arc = Math.max(0.01, ((step - gap) / 360) * CIRCUMFERENCE);
  const stroke = Math.min(RING.stroke, ((step - 3) / 360) * CIRCUMFERENCE);
  const circle = {
    cx: CENTER,
    cy: CENTER,
    r: RADIUS,
    fill: 'none',
    strokeWidth: stroke,
    strokeLinecap: 'round',
    strokeDasharray: `${arc} ${CIRCUMFERENCE}`,
  };
  return (
    <div
      role={label ? 'img' : undefined}
      aria-label={label}
      className="relative flex size-[76px] shrink-0 items-center justify-center"
    >
      <svg
        viewBox={`0 0 ${RING.size} ${RING.size}`}
        className="absolute inset-0"
        aria-hidden="true"
      >
        {Array.from({ length: arcs }, (_, index) => {
          const tint =
            index < green
              ? 'stroke-mint'
              : index < green + orange
                ? 'stroke-primary'
                : null;
          return (
            <g
              key={index}
              transform={`rotate(${-90 + gap / 2 + index * step} ${CENTER} ${CENTER})`}
            >
              <circle {...circle} className="stroke-info-light" />
              {tint && (
                <circle
                  {...circle}
                  className={`${tint} motion-safe:animate-ringArc`}
                  style={{
                    '--arc': `${arc}px`,
                    animationDelay: `${index * Math.min(140, 1400 / arcs)}ms`,
                  }}
                />
              )}
            </g>
          );
        })}
      </svg>
      <span className="relative flex items-end font-title" aria-hidden="true">
        <span
          data-testid="ring-done"
          className="text-xl font-bold leading-7 text-ink"
        >
          {done}
        </span>
        <span className="text-sm font-medium leading-5 text-muted">
          /{total}
        </span>
      </span>
    </div>
  );
};

export default CallsRing;
